import { Button, Container, Element } from '@playcanvas/pcui';

import { Events } from '../events';
import { RfiRecord } from './rfi-panel';
import annotationFlytoSvg from './svg/annotation-flyto.svg';
import annotationListSvg from './svg/annotation-list.svg';
import { Tooltips } from './tooltips';
import { isVolugraphCommerceMode } from '../volugraph-commerce';

const createSvg = (svgString: string) => {
    const decodedStr = decodeURIComponent(svgString.substring('data:image/svg+xml,'.length));
    return new DOMParser().parseFromString(decodedStr, 'image/svg+xml').documentElement;
};

class LeftAnnotationToolbar extends Container {
    constructor(events: Events, tooltips: Tooltips, args = {}) {
        super({ ...args, id: 'left-annotation-toolbar' });

        this.dom.addEventListener('pointerdown', (event) => {
            event.stopPropagation();
        });

        // "T+" Add Annotation button (always at top)
        const addPin = new Button({ id: 'lat-add-pin', class: 'lat-tool' });
        addPin.dom.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><text x="4" y="17" font-size="16" font-weight="700" font-family="Arial,sans-serif" fill="currentColor" stroke="none">T</text><line x1="18" y1="8" x2="18" y2="16"/><line x1="14" y1="12" x2="22" y2="12"/></svg>';

        const flyTo = new Button({ id: 'lat-flyto', class: 'lat-tool' });
        const toggleList = new Button({ id: 'lat-list', class: ['lat-tool', 'lat-toggle'] });

        flyTo.dom.appendChild(createSvg(annotationFlytoSvg));
        toggleList.dom.appendChild(createSvg(annotationListSvg));

        const annotationGroup = new Container({ class: 'lat-annotation-group' });
        annotationGroup.append(addPin);
        annotationGroup.append(flyTo);
        annotationGroup.append(toggleList);
        this.append(annotationGroup);

        const commerceMode = isVolugraphCommerceMode();

        if (!commerceMode) {
            this.append(new Element({ class: 'lat-separator' }));
        }

        const boundaryPoly = new Button({ id: 'lat-boundary', class: 'lat-tool' });
        boundaryPoly.dom.innerHTML =
            '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
            '<path d="M12 3 20 8v8l-8 5-8-5V8l8-5z" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linejoin="round" />' +
            '</svg>';

        if (!commerceMode) {
            this.append(boundaryPoly);
        }

        /** Bottom block: divider + add coordinates — margin-top:auto pins to toolbar bottom */
        let coordBtn: Button | null = null;
        let coordActiveHint: HTMLDivElement | null = null;

        if (!commerceMode) {
            coordBtn = new Button({ id: 'lat-coord', class: 'lat-tool' });
            coordBtn.dom.innerHTML =
                '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
                '<circle cx="12" cy="12" r="3.25" stroke="currentColor" stroke-width="1.75" fill="none" />' +
                '<path d="M12 2v4M12 18v4M2 12h4M18 12h4" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" fill="none" />' +
                '</svg>';
            const coordStack = new Container({ class: 'lat-coord-stack' });
            coordStack.append(new Element({ class: 'lat-separator' }));

            const coordToolWrap = document.createElement('div');
            coordToolWrap.className = 'lat-coord-tool-wrap';
            coordToolWrap.appendChild(coordBtn.dom);

            coordActiveHint = document.createElement('div');
            coordActiveHint.className = 'lat-coord-active-hint';
            coordActiveHint.hidden = true;
            coordActiveHint.setAttribute('aria-hidden', 'true');
            coordActiveHint.innerHTML =
                '<span class="lat-coord-active-hint__line">Coordinate marker selected</span>' +
                '<span class="lat-coord-active-hint__line lat-coord-active-hint__line--sub">Based on map alignment setup</span>';
            coordToolWrap.appendChild(coordActiveHint);

            coordStack.dom.appendChild(coordToolWrap);
            this.append(coordStack);
        }

        let isAddMode = false;
        let isCoordMode = false;
        let isBoundaryMode = false;
        let hasAnnotations = false;

        const syncAvailability = () => {
            const selected = events.invoke('annotation.hasSelection') as boolean;
            const rec = events.invoke('annotation.getSelected') as RfiRecord | null;
            toggleList.enabled = hasAnnotations;
            flyTo.enabled = hasAnnotations && !!selected;
        };

        addPin.dom.addEventListener('click', () => {
            isAddMode = !isAddMode;
            if (isAddMode) {
                events.fire('tool.rfi');
            } else {
                events.fire('tool.deactivate');
            }
        });

        flyTo.dom.addEventListener('click', () => {
            events.fire('annotation.flyToSelected');
        });

        toggleList.dom.addEventListener('click', () => {
            events.fire('annotation.toggleListModal');
        });

        boundaryPoly.dom.addEventListener('click', () => {
            if (isBoundaryMode) {
                events.fire('tool.deactivate');
            } else {
                events.fire('tool.boundaryPolygon');
            }
        });

        if (coordBtn) {
            coordBtn.dom.addEventListener('click', () => {
                const ok = events.invoke('volugraph.coord.hasGoodGisAlignment') === true;
                if (!ok) return;
                isCoordMode = !isCoordMode;
                if (isCoordMode) {
                    events.fire('tool.coord');
                } else {
                    events.fire('tool.deactivate');
                }
            });

            const COORD_TOOLTIP_READY = 'Add coordinate marker';
            const COORD_TOOLTIP_BLOCK = 'Add Coordinate Markers: COMPLETE GIS ALIGNMENT FIRST';

            const syncCoordGateUi = () => {
                let ok = false;
                try {
                    ok = events.invoke('volugraph.coord.hasGoodGisAlignment') === true;
                } catch {
                    ok = false;
                }
                tooltips.unregister(coordBtn);
                tooltips.register(coordBtn, ok ? COORD_TOOLTIP_READY : COORD_TOOLTIP_BLOCK, 'right');
                coordBtn.dom.classList.toggle('lat-coord--locked', !ok);
            };

            events.on('volugraph.coordGateUpdated', syncCoordGateUi);

            const syncCoordActiveHint = (toolName: string | null) => {
                if (!coordActiveHint) return;
                const on = toolName === 'coord';
                coordActiveHint.hidden = !on;
                coordActiveHint.setAttribute('aria-hidden', on ? 'false' : 'true');
                coordActiveHint.classList.toggle('is-visible', on);
            };

            events.on('tool.activated', (toolName: string | null) => {
                isAddMode = toolName === 'rfi';
                isCoordMode = toolName === 'coord';
                isBoundaryMode = toolName === 'boundaryPolygon';
                addPin.class[toolName === 'rfi' ? 'add' : 'remove']('active');
                coordBtn.class[toolName === 'coord' ? 'add' : 'remove']('active');
                boundaryPoly.class[toolName === 'boundaryPolygon' ? 'add' : 'remove']('active');
                syncCoordActiveHint(toolName);
                syncCoordGateUi();
            });

            syncCoordGateUi();
        } else if (!commerceMode) {
            events.on('tool.activated', (toolName: string | null) => {
                isAddMode = toolName === 'rfi';
                isBoundaryMode = toolName === 'boundaryPolygon';
                addPin.class[toolName === 'rfi' ? 'add' : 'remove']('active');
                boundaryPoly.class[toolName === 'boundaryPolygon' ? 'add' : 'remove']('active');
            });
        }

        events.on('annotation.listVisible', (visible: boolean) => {
            toggleList.class[visible ? 'add' : 'remove']('active');
        });

        events.on('annotation.selectionChanged', syncAvailability);
        events.on('annotation.countChanged', (count: number) => {
            hasAnnotations = count > 0;
            syncAvailability();
        });

        syncAvailability();

        tooltips.register(addPin, 'Add annotation', 'right');
        tooltips.register(flyTo, 'Fly to selected', 'right');
        tooltips.register(toggleList, 'Annotation list', 'right');
        if (!commerceMode) {
            tooltips.register(boundaryPoly, 'Boundary polygon — draw, select edge, Delete to remove', 'right');
        }
    }
}

export { LeftAnnotationToolbar };
